import { RefObject, useState } from "react";
import { faWalking, faBoltLightning, faTruckFast } from "@fortawesome/pro-light-svg-icons";
import cx from "classnames";
import { IconDefinition, faTag } from "@fortawesome/pro-solid-svg-icons";

import { SubscriptionFrequency, GroupedItemFlavors } from "common/types";
import { toDollars, toPercent } from "common/utils/formatting";
import { Optional } from "common/utils/types";
import { frequencyValueToString, stringToFrequencyValue } from "common/utils/subscriptions";
import { capitalized } from "common/utils/strings";
import { parseNumber } from "common/utils/numbers";
import { DeliveryMethodKind, ItemFulfillmentOption } from "common/types/fulfillments";
import Icon from "common/components/Icon";
import Image from "common/components/Image";
import Spinner from "common/components/Spinner";
import { SetFetchError } from "common/queries/types";
import { deepCombineErrors } from "common/utils/errors";
import { dedupeByKey } from "common/utils/entities";

import SelectButton from "client/common/components/buttons/SelectButton";
import Button from "client/common/components/buttons/Button";
import QuantityInput from "client/common/components/inputs/QuantityInput";
import Select from "client/common/components/inputs/Select";
import SelectPanel from "client/common/components/inputs/SelectPanel";
import RichSelect from "client/common/components/inputs/RichSelect";
import Modal from "client/common/components/deprecated-modals/Modal";
import Markdown from "client/common/components/content/Markdown";
import Loading from "client/common/components/loading/Loading";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import Banner from "client/common/components/content/Banner";
import CrossedPrice from "client/common/components/content/CrossedPrice";
import { useClientQuery } from "client/accounts/hooks";
import { IClient } from "client/accounts/types";
import PillButton from "client/common/components/buttons/PillButton";
import TextButton from "client/common/components/buttons/TextButton";
import {
  useInStockNotificationCreateMutation,
  useInStockNotificationDeleteMutation,
} from "client/inbox/hooks";
import { InStockNotification } from "client/inbox/types";
import { useBreakpoints } from "client/common/hooks";
import { urls } from "client/routes";
import { ICartItemCreate } from "client/carts/types";
import Alert from "client/common/components/content/Alert";
import { useSubscriptionInfoDrawer } from "client/subscriptions/stackables";

import {
  IProductItem,
  IProductLineDetails,
  IPDPProduct,
  SubscriptionSelectItemProps,
} from "../types";
import { getItemPriceFromQuantity, calculateDosePrice, hasDosePricing } from "../utils";
import { useScrollToItemSelection } from "../hooks/utils";

import styles from "../styles/ProductLineSelections.module.less";

const getOptionColumnSizeClassName = (options: unknown[]) => {
  if (options.length === 4 || options.length <= 2) {
    return styles.double;
  }

  return styles.triple;
};

interface SelectionOption {
  image?: string;
  label: string | number;
  description: string;
  is_available: boolean;
}

interface SelectIdOption extends SelectionOption {
  label: string;
  id: number | undefined;
}

type ProductLineSelectionsProps = {
  children: React.ReactNode;
  className?: string;
};

const ProductLineSelections = ({ children, className = "" }: ProductLineSelectionsProps) => {
  return <div className={className}>{children}</div>;
};

type ProductSelectProps = {
  selectionRef: RefObject<HTMLDivElement | null>;
  productLine: IProductLineDetails;
  products: IPDPProduct[];
  onChange: (id: IPDPProduct) => void;
  value: Optional<IPDPProduct>;
};

const ProductSelectControl = (props: ProductSelectProps) => {
  const { products, value, onChange } = props;

  const { isTabletPortraitAndSmaller } = useBreakpoints();

  const { scrollRef, setRef } = useScrollToItemSelection(
    value?.id ?? 0,
    isTabletPortraitAndSmaller,
  );

  // Only show images if all the products have them for alignment
  const hasAllImages = products.every((product) => !!product.preview_image);

  const renderProduct = (product: Optional<IPDPProduct>) => {
    if (!product) {
      return (
        <div className={styles.placeholderProduct}>
          <div className={styles.placeholderProductText}>Please select an available product...</div>
        </div>
      );
    }
    return (
      <div className={cx(styles.product, !hasAllImages && styles.hideImages)}>
        <Image className={styles.image} src={product.preview_image} alt="Product" />
        <div className={styles.text}>
          <div className={styles.name}>{product.label}</div>
          <div className={styles.description}>{product.description}</div>
        </div>
      </div>
    );
  };

  const renderSelectButton = (product: IPDPProduct) => (
    <SelectButton
      className={styles.button}
      isActive={value?.id === product.id}
      onClick={() => onChange(product)}
      align="center"
      renderContent={(p) =>
        !hasAllImages ? (
          <SelectButton.DefinitionContent
            label={product.label}
            description={product.description}
            {...p}
            status={null}
          />
        ) : (
          renderProduct(product)
        )
      }
    />
  );

  // Don't use non-native selects on mobile since they're hard to use
  if (isTabletPortraitAndSmaller) {
    // Give better alignment with few products to match other selections below
    if (products.length <= 2) {
      return (
        <div className={`${styles.options} ${styles.double}`}>
          {products.map((product) => (
            <div className={styles.option} key={product.id}>
              {renderSelectButton(product)}
            </div>
          ))}
        </div>
      );
    }

    // Don't wrap, but horizontal scroll these products when there are many
    return (
      <HorizontalOverflow.Contained
        scrollRef={scrollRef}
        classNames={{
          container: styles.overflow,
          scroller: styles.buttons,
          button: styles.arrow,
          back: styles.back,
          next: styles.next,
        }}
      >
        {products.map((product) => (
          <div className={styles.option} key={product.id} ref={(ref) => setRef(product.id, ref)}>
            {renderSelectButton(product)}
          </div>
        ))}
      </HorizontalOverflow.Contained>
    );
  }

  // Use the rich select on desktop to surface additional content
  return (
    <div className={styles.select}>
      <RichSelect
        onChange={onChange}
        getKey={(option) => option.id}
        value={value}
        options={products}
        renderOption={renderProduct}
      />
    </div>
  );
};

const ProductSelect = (props: ProductSelectProps) => {
  const { productLine, selectionRef } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className={styles.group} ref={selectionRef}>
      <div className={styles.header}>
        <h6 className={styles.label}>Choose your {productLine.product_select_label}</h6>
        {!!productLine.dosage_guide && (
          <button type="button" onClick={() => setIsModalOpen(true)} className={styles.sublabel}>
            <span className={styles.long}>
              What {productLine.product_select_label} should I choose?
            </span>
            <span className={styles.short}>
              {capitalized(productLine.product_select_label)} guide
            </span>
          </button>
        )}
      </div>
      <ProductSelectControl {...props} />
      <Modal visible={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalLayout>
          <ModalLayout.Header
            title={`Which ${productLine.product_select_label}?`}
            onClose={() => setIsModalOpen(false)}
          />
          <ModalLayout.Content>
            <Markdown markdown={productLine.dosage_guide} />
          </ModalLayout.Content>
        </ModalLayout>
      </Modal>
    </div>
  );
};

type ItemSelectProps = {
  productLine: IProductLineDetails;
  items: IProductItem[];
  onChange: (id: IProductItem) => void;
  value: Optional<IProductItem>;
  subscription: SubscriptionFrequency | null;
  areFeesIncludedInSingleDispense?: boolean;
};

const ItemSelectControl = ({
  items,
  productLine,
  value,
  onChange,
  subscription,
  areFeesIncludedInSingleDispense = false,
}: ItemSelectProps) => {
  const { isTabletPortraitAndSmaller } = useBreakpoints();

  const { scrollRef, setRef } = useScrollToItemSelection(
    value?.id ?? 0,
    isTabletPortraitAndSmaller,
  );

  // Only show descriptions if all items have descriptions
  const hasAllDescriptions = items.every(
    (item) => !!(productLine.is_compound ? item.compound_description : item.description),
  );
  // Only show dose pricing if all items have dose pricing (units_count available)
  const hasAllDosePricing = items.every((item) => hasDosePricing(item));

  const renderPricing = (item: IProductItem) => {
    if (item.is_single_dispense && areFeesIncludedInSingleDispense) {
      return <span>Price varies with quantity</span>;
    }
    return hasAllDosePricing ? (
      <span>
        {toDollars(
          subscription
            ? calculateDosePrice(item.subscription_discounted_price, item.units_count)
            : calculateDosePrice(item.non_subscription_discounted_price, item.units_count),
        )}
        /{item.dose_label}
      </span>
    ) : (
      <span>
        {toDollars(
          subscription
            ? item.subscription_discounted_price
            : item.non_subscription_discounted_price,
        )}
      </span>
    );
  };

  const renderSelectButton = (item: IProductItem) => (
    <SelectButton
      key={item.id}
      className={styles.button}
      isActive={item.id === value?.id}
      onClick={() => onChange(item)}
      renderContent={(props) => (
        <SelectButton.DefinitionContent
          {...props}
          label={item.label}
          description={
            <>
              {hasAllDescriptions && (
                <div>
                  {productLine.is_compound && item.compound_description
                    ? item.compound_description
                    : item.description}
                </div>
              )}
              <div>{renderPricing(item)}</div>
              {item.has_promotions && (
                <div className={styles.discount}>
                  <Icon icon={faTag} />
                </div>
              )}
            </>
          }
          status={null}
        />
      )}
    />
  );

  // Show an alternative UI for a less overwhelming display when there are too many items
  // or if it's a compound product line
  if (items.length > 6 || productLine.is_compound) {
    // Mobile gets a swipeable list to match product select
    if (isTabletPortraitAndSmaller) {
      return (
        <HorizontalOverflow.Contained
          classNames={{
            container: styles.overflow,
            scroller: styles.buttons,
            button: styles.arrow,
            back: styles.back,
            next: styles.next,
          }}
          scrollRef={scrollRef}
        >
          {items.map((item) => (
            <div key={item.id} ref={(ref) => setRef(item.id, ref)}>
              {renderSelectButton(item)}
            </div>
          ))}
        </HorizontalOverflow.Contained>
      );
    }

    // Desktop gets a select to match product select
    return (
      <RichSelect
        onChange={onChange}
        getKey={(option) => option.id}
        value={value}
        options={items}
        renderOption={(item) =>
          item ? (
            <div className={styles.item}>
              <div className={styles.name}>
                {item.label}
                {/* For compounds, the description is extraneous because it matches the product info */}
                {hasAllDescriptions && !productLine.is_compound && (
                  <span> · {item.description}</span>
                )}
              </div>
              <div className={styles.description}>
                {renderPricing(item)}{" "}
                {productLine.is_compound && item.compound_description && (
                  <span> · {item.compound_description}</span>
                )}
              </div>
            </div>
          ) : (
            <div>Please select a valid item...</div>
          )
        }
      />
    );
  }

  // Most will just render as a nicely formatted grid
  return (
    <div className={`${styles.options} ${getOptionColumnSizeClassName(items)}`}>
      {items.map(renderSelectButton)}
    </div>
  );
};

const ItemSelect = (props: ItemSelectProps) => {
  const { productLine } = props;

  return (
    <div className={styles.group}>
      <h6 className={styles.label}>Choose your {productLine.item_select_label}</h6>
      <ItemSelectControl {...props} />
    </div>
  );
};

type FulfillmentOptionSelectProps = {
  name: string;
  description: string;
  icon: IconDefinition;
  value: DeliveryMethodKind;
  fulfillment: ItemFulfillmentOption;
};

const getFulfillmentOptionSelectProps = (option: ItemFulfillmentOption) => {
  const mapping: Record<DeliveryMethodKind, FulfillmentOptionSelectProps> = {
    [DeliveryMethodKind.Pickup]: {
      name: "Pickup from clinic",
      description: "Collect your items directly from the clinic",
      icon: faWalking,
      value: DeliveryMethodKind.Pickup,
      fulfillment: option,
    },
    [DeliveryMethodKind.Shipping]: {
      name: "Ship to home",
      description: "Select shipping speed at checkout",
      icon: faTruckFast,
      value: DeliveryMethodKind.Shipping,
      fulfillment: option,
    },
    [DeliveryMethodKind.Delivery]: {
      name: "2-hour instant delivery",
      description: "Delivered by Shipt",
      icon: faBoltLightning,
      value: DeliveryMethodKind.Delivery,
      fulfillment: option,
    },
  };
  return mapping[option.kind];
};

type DeliveryMethodSelectPanelProps = {
  fulfillmentOptions: ItemFulfillmentOption[];
  value: DeliveryMethodKind;
  onChange: (value: DeliveryMethodKind) => void;
  showUnavailableOptions?: boolean;
};

const DeliveryMethodSelectPanel = ({
  fulfillmentOptions,
  value,
  onChange,
  showUnavailableOptions = false,
}: DeliveryMethodSelectPanelProps) => {
  const options = fulfillmentOptions
    .filter((option) => option.is_available || showUnavailableOptions)
    .map(getFulfillmentOptionSelectProps);

  return (
    <SelectPanel
      getKey={(option) => `${option?.value}`}
      value={options.find((option) => option.value === value)}
      options={options}
      onChange={(option) => onChange(option.value)}
      renderOption={(option) => (
        <div className={styles.method}>
          <Icon fixedWidth={true} icon={option.icon} className={styles.icon} />
          <div className={styles.text}>
            <div className={styles.name}>{option.name}</div>
            <div className={styles.description}>{option.description}</div>
          </div>
        </div>
      )}
    />
  );
};

type DeliveryMethodSelectProps = DeliveryMethodSelectPanelProps & {
  messages: string[];
};

const DeliveryMethodSelect = (props: DeliveryMethodSelectProps) => {
  const { messages } = props;

  return (
    <div className={styles.group}>
      <h6 className={styles.label}>Choose Delivery Method</h6>
      <DeliveryMethodSelectPanel {...props} />
      {messages.map((message) => (
        <Banner variant="primary" key={message}>
          <Banner.Text align="left" label={message} />
        </Banner>
      ))}
    </div>
  );
};

const getAutoshipNameDescription = (item: SubscriptionSelectItemProps, client?: IClient) => {
  // sometimes an item is eligible for autoship discount through a rebate, not through clinic incentive
  const hasClinicAutoshipIncentive =
    item.eligible_for_autoship_discounts &&
    parseNumber(item.subscription_incentive_discount_percentage) > 0;

  const clinicAutoshipMessage = item.hide_subscription_discount_percentage
    ? "Extra savings"
    : `Save up to an extra ${toPercent(item.subscription_incentive_discount_percentage)}`;

  if (hasClinicAutoshipIncentive) {
    if (client?.recurring_subscription_eligible_for_clinic) {
      return {
        name: "Autoship & save",
        description: `${clinicAutoshipMessage} on this order with automatic refills`,
      };
    }
    return {
      name: "Autoship",
      description: "Never miss a dose with scheduled deliveries",
    };
  }
  return {
    name: "Autoship",
    description: `Never miss a dose with scheduled deliveries${
      !item.eligible_for_autoship_discounts
        ? " (not eligible for autoship savings due to manufacturer restrictions)"
        : ""
    }`,
  };
};

type SubscriptionSelectProps = {
  item: SubscriptionSelectItemProps;
  onChange: (value: SubscriptionFrequency | null) => void;
  value: SubscriptionFrequency | null;
  quantity: number;
};

const SubscriptionSelectPanel = ({ item, onChange, value, quantity }: SubscriptionSelectProps) => {
  const { data: client } = useClientQuery();

  const autoshipOptions = dedupeByKey(
    [...(value ? [value] : []), item.recommended_frequency, ...item.frequency_options].map(
      (option) => ({ option, key: frequencyValueToString(option) }),
    ),
    "key",
    "last",
  );

  const recommendedKey = frequencyValueToString(item.recommended_frequency);
  const options = [
    {
      name: getAutoshipNameDescription(item, client).name,
      description: getAutoshipNameDescription(item, client).description,
      price: item.subscription_discounted_price,
      isSelected: value !== null,
      value: value || item.recommended_frequency || null,
      renderExpandedContent: () =>
        value ? (
          <Select.Simple
            value={frequencyValueToString(value)}
            onChange={(event) => onChange(stringToFrequencyValue(event.target.value))}
          >
            {autoshipOptions.map(({ option, key }) => (
              <Select.Option value={key} key={key}>
                {`${option.display}${key === recommendedKey ? " (recommended)" : ""}`}
              </Select.Option>
            ))}
          </Select.Simple>
        ) : (
          <Loading />
        ),
    },
    {
      name: "One time purchase",
      description: "Purchase just this once",
      price: item.non_subscription_discounted_price,
      isSelected: value === null,
      value: null,
    },
  ];

  return (
    <SelectPanel
      getKey={(option) => `${option?.isSelected}`}
      value={options.find((option) => option.isSelected)}
      options={options}
      onChange={(option) => onChange(option.value)}
      renderOption={(option) => (
        <div className={styles.subscription}>
          <div className={styles.names}>
            <span className={styles.name}>{option.name}</span>
            {!!option.price && (
              <CrossedPrice
                crossed={item?.non_subscription_non_discounted_price}
                price={getItemPriceFromQuantity(
                  item,
                  quantity,
                  option.value,
                  item.price_breaks ? item.price_breaks : [],
                )}
                classNames={{
                  container: styles.pricing,
                  price: styles.name,
                  crossed: styles.description,
                }}
              />
            )}
          </div>
          <div className={styles.description}>{option.description}</div>
        </div>
      )}
      getState={(option) => ({
        renderExpandedContent: option.renderExpandedContent,
      })}
    />
  );
};

const SubscriptionSelect = (props: SubscriptionSelectProps) => {
  const { item } = props;

  const { open: openSubscriptionInfo } = useSubscriptionInfoDrawer();

  if (!item.is_subscribable) {
    return null;
  }

  return (
    <div className={styles.group}>
      <div className={styles.header}>
        <h6 className={styles.label}>Choose Frequency</h6>
        <button type="button" className={styles.sublabel} onClick={openSubscriptionInfo}>
          Learn more
        </button>
      </div>
      <SubscriptionSelectPanel {...props} />
    </div>
  );
};

type OptionSelectProps = {
  optionsGroup: GroupedItemFlavors;
  onChange: (id: SelectIdOption["id"]) => void;
  value: Optional<SelectIdOption["id"]>;
};

const OptionSelectControl = (props: OptionSelectProps) => {
  const { optionsGroup, onChange, value } = props;
  const { options } = optionsGroup;

  // Too many options, just render a native select since just a string needs to be displayed
  if (options.length > 6) {
    return (
      <Select
        // This extra label is redundant, but should be rare we're here anyway
        label={optionsGroup.label}
        value={`${value}`}
        onChange={(v) => onChange(parseNumber(v, undefined))}
      >
        {options.map((option) => (
          <Select.Option key={option.id} value={`${option.id}`}>
            {option.display}
          </Select.Option>
        ))}
      </Select>
    );
  }

  // Most will be the consistent select buttons to match other inputs
  return (
    <div className={`${styles.options} ${getOptionColumnSizeClassName(options)}`}>
      {options.map((option) => (
        <SelectButton
          key={option.id}
          align="center"
          isActive={option.id === value}
          onClick={() => onChange(option.id)}
          renderContent={(p) => (
            <SelectButton.DefinitionContent {...p} label={option.display} status={null} />
          )}
        />
      ))}
    </div>
  );
};

const OptionSelect = (props: OptionSelectProps) => {
  const { optionsGroup } = props;

  return (
    <div className={styles.group}>
      <h6 className={styles.label}>Choose a {optionsGroup.label.toLowerCase()}</h6>
      <OptionSelectControl {...props} />
    </div>
  );
};

type AddSectionProps = { children: React.ReactNode; error: SetFetchError<ICartItemCreate> | null };

const AddSection = ({ children, error }: AddSectionProps) => {
  return (
    <div className={styles.addSectionContainer}>
      {error && <Alert variant="danger">{deepCombineErrors(error.errors)}</Alert>}
      <div className={styles.addSection}>{children}</div>
    </div>
  );
};

type QuantitySelectProps = {
  value: number;
  max?: Optional<number>;
  onChange: (value: number) => void;
};

const QuantitySelect = (props: QuantitySelectProps) => {
  const { value, onChange, max } = props;

  return (
    <QuantityInput
      className={styles.quantity}
      min={1}
      onChange={onChange}
      value={value}
      max={max}
    />
  );
};

type AddToCartButtonProps = {
  onSubmit: () => void;
  isLoading: boolean;
  discountedSubtotal: number;
  disabled: boolean;
};

const AddToCartButton = (props: AddToCartButtonProps) => {
  const { onSubmit, discountedSubtotal, isLoading, disabled } = props;

  return (
    <Button
      className={styles.submit}
      onClick={onSubmit}
      isLoading={isLoading}
      disabled={disabled || !discountedSubtotal}
    >
      Add item
      {!!discountedSubtotal && (
        <span className={styles.long}>&nbsp;· {toDollars(discountedSubtotal)}</span>
      )}
    </Button>
  );
};

type UnavailableItem = {
  id: number;
  is_backordered: boolean;
  in_stock_notification: Optional<InStockNotification>;
  is_vendor_fulfilled: boolean;
};

type UnavailableProps = {
  item: UnavailableItem;
  deliveryMethod: Optional<DeliveryMethodKind>;
  onNotificationChange: () => void;
  className?: string;
};

const Unavailable = ({
  item,
  deliveryMethod,
  onNotificationChange,
  className,
}: UnavailableProps) => {
  const { data: client } = useClientQuery();

  // Keep track of the notification internally so that consumers don't have to invalidate
  // deeply nested item serializations just for stock notification CRUD
  const [notification, setNotification] = useState(item?.in_stock_notification);

  const { mutate: createNotification, isLoading: isCreating } =
    useInStockNotificationCreateMutation({
      onSuccess: (n) => {
        setNotification(n);
        onNotificationChange();
      },
    });
  const { mutate: deleteNotification, isLoading: isDeleting } =
    useInStockNotificationDeleteMutation({
      onSuccess: () => {
        setNotification(undefined);
        onNotificationChange();
      },
    });

  const getDescription = () => {
    if (!client) {
      return (
        <span>
          <TextButton dest={urls.accounts.lookup()} hasUnderline={true}>
            Sign in or create an account
          </TextButton>{" "}
          to be notified via email when this item is back in stock.
        </span>
      );
    }

    if (!client.primary_contact_email?.email) {
      return (
        <span>
          <TextButton dest={urls.accounts.detail()} hasUnderline={true}>
            Add an email
          </TextButton>{" "}
          to your account to be notified when this item is back in stock.
        </span>
      );
    }

    if (notification) {
      return (
        <span>
          An email will be sent to <strong>{client.primary_contact_email?.email}</strong> when this
          item is back in stock.{" "}
          {isDeleting ? (
            <Spinner />
          ) : (
            <>
              Click{" "}
              <TextButton
                onClick={() => notification && deleteNotification(notification.id)}
                hasUnderline={true}
              >
                here
              </TextButton>{" "}
              to cancel.
            </>
          )}
        </span>
      );
    }

    return (
      <>
        This item is not currently available. Consider speaking with your veterinarian about
        alternatives.
        <PillButton
          onClick={() => createNotification({ item: item?.id ?? -1 })}
          variant="secondary"
          size="xs"
          isLoading={isCreating}
        >
          Notify me when available
        </PillButton>
      </>
    );
  };

  const deliveryKindToTitle: Record<DeliveryMethodKind, string> = {
    [DeliveryMethodKind.Delivery]: "2-hour instant delivery",
    [DeliveryMethodKind.Pickup]: "clinic pickup",
    [DeliveryMethodKind.Shipping]: "home delivery",
  };

  return (
    <Banner className={className}>
      <Banner.Content
        label={`Out of stock for ${
          deliveryMethod ? deliveryKindToTitle[deliveryMethod] : "shipping and pickup"
        }`}
        description={
          // Can only sign up for notifications for vendor-fulfilled
          deliveryMethod === DeliveryMethodKind.Shipping &&
          item?.is_vendor_fulfilled && (
            <div className={styles.unavailableDescription}>
              {item.is_backordered && (
                <p>
                  This item is out of stock and on backorder by the manufacturer. It may be an
                  extended period of time until this item is available. Consider speaking with your
                  veterinarian about alternatives.
                </p>
              )}
              {getDescription()}
            </div>
          )
        }
      />
    </Banner>
  );
};

type LowStockProps = {
  className?: string;
};

const LowStock = ({ className }: LowStockProps) => {
  return (
    <Banner className={className}>
      <Banner.Content label="Low stock - this item may sell out soon" />
    </Banner>
  );
};

export default Object.assign(ProductLineSelections, {
  ProductSelect,
  ItemSelect,
  DeliveryMethodSelect,
  DeliveryMethodSelectPanel,
  SubscriptionSelect,
  SubscriptionSelectPanel,
  QuantitySelect,
  OptionSelect,
  AddToCartButton,
  AddSection,
  Unavailable,
  LowStock,
});
